import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { Box, Button, IconButton, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ScoutIcon from "components/ScoutIcon";
import { useThemeGreyBackgroundColor } from "hooks/useThemeGreyBackgroundColor";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from "react";

import type { StreamStatus } from "../../../../stream";
import type { ScoutActiveTurn } from "../hooks/types";
import type { ScoutConversationPrivateDocumentRow } from "../hooks/useScoutConversationPrivateDocuments";
import type { ScoutAttachment } from "../scoutSubmitAttachments";

import { canRetryLastUserTurn, getLastUserTurnForRetry } from "../scoutRetryPayload";
import { AgentMessage } from "./message/AgentMessage";
import { getTurnErrorMessage, isCurrentAgentTurnComplete } from "./message/scoutAgentTurnUi";
import { type ScoutChatMessage } from "./message/scoutChatMessage";
import { UserMessage } from "./message/UserMessage";
import { ScoutChatDropzone } from "./ScoutChatDropzone";
import { ScoutInput, type ScoutSubmitHandler } from "./ScoutInput";
import { ScoutLargeDocumentOptInButtons } from "./ScoutLargeDocumentOptInButtons";

// Errors thrown from the GraphQL `SendScoutMessage` mutation (e.g. the API
// couldn't accept the message at all) surface through a different code path
// — we don't want to also render an inline retry bubble for them. Match the
// known string to keep the in-chat retry experience scoped to mid-turn
// failures where Scout actually started a turn and then couldn't finish.
const COULD_NOT_START_ERROR_MESSAGE = "Scout could not start this conversation.";

const ScoutDisclaimer = memo(function ScoutDisclaimer() {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block", textAlign: "center", mt: 1.5, px: 2 }}
    >
      Scout is AI and can make mistakes. Please double-check responses.
    </Typography>
  );
});
ScoutDisclaimer.displayName = "ScoutDisclaimer";

const ChatFooter = memo(
  function ChatFooter({
    tenantId,
    conversationId,
    largeDocumentOptIns,
    showLargeDocumentOptInButtons,
    onDismissLargeDocumentOptIn,
    onConfirmLargeDocumentAnalysis,
    isConfirmingLargeDocumentAnalysis,
    isDecliningLargeDocumentAnalysis,
    onSubmit,
    onAbort,
    isSending,
    isAborting,
    autoFocus,
    onAttachmentsUploaded,
    onAttachmentRemoved,
    allowMutations,
    onAllowMutationsChange,
    lastUserMessage,
  }: {
    tenantId?: string;
    conversationId?: string;
    largeDocumentOptIns: ScoutConversationPrivateDocumentRow[];
    showLargeDocumentOptInButtons: boolean;
    onDismissLargeDocumentOptIn?: (privateDocumentIds: string[]) => void | Promise<void>;
    onConfirmLargeDocumentAnalysis?: (input: {
      privateDocumentIds: string[];
      documents: ScoutConversationPrivateDocumentRow[];
    }) => void | Promise<void>;
    isConfirmingLargeDocumentAnalysis?: boolean;
    isDecliningLargeDocumentAnalysis?: boolean;
    onSubmit: ScoutSubmitHandler;
    onAbort: () => void;
    isSending: boolean;
    isAborting: boolean;
    autoFocus: boolean;
    onAttachmentsUploaded?: (attachments: ScoutAttachment[]) => void;
    onAttachmentRemoved?: (privateDocumentId: string) => void | Promise<void>;
    allowMutations?: boolean;
    onAllowMutationsChange?: (next: boolean) => void;
    lastUserMessage?: string;
  }) {
    const canvasColor = useThemeGreyBackgroundColor();

    return (
      <Box
        component="footer"
        sx={{
          flexShrink: 0,
          p: 2,
          pt: 0,
          bgcolor: canvasColor,
          pointerEvents: "none",
        }}
      >
        <Box sx={{ pointerEvents: "auto" }}>
          {showLargeDocumentOptInButtons && largeDocumentOptIns.length > 0 ? (
            <ScoutLargeDocumentOptInButtons
              documents={largeDocumentOptIns}
              isConfirming={isConfirmingLargeDocumentAnalysis ?? false}
              isDeclining={isDecliningLargeDocumentAnalysis ?? false}
              onConfirm={(privateDocumentIds) => {
                void (onConfirmLargeDocumentAnalysis ?? (async () => {}))({
                  privateDocumentIds,
                  documents: largeDocumentOptIns,
                });
              }}
              onAskWithoutFullAnalysis={() => {
                void onDismissLargeDocumentOptIn?.(
                  largeDocumentOptIns.map((document) => document.id)
                );
              }}
            />
          ) : null}
          <ScoutInput
            tenantId={tenantId}
            conversationId={conversationId}
            onSubmit={onSubmit}
            onAbort={onAbort}
            isSending={isSending}
            isAborting={isAborting}
            onAttachmentsUploaded={onAttachmentsUploaded}
            onAttachmentRemoved={onAttachmentRemoved}
            placeholder="Ask Scout a follow-up question"
            autoFocus={autoFocus}
            lastUserMessage={lastUserMessage}
            allowMutations={allowMutations}
            onAllowMutationsChange={onAllowMutationsChange}
          />
        </Box>
        <ScoutDisclaimer />
      </Box>
    );
  },
  (prev, next) =>
    prev.tenantId === next.tenantId &&
    prev.conversationId === next.conversationId &&
    prev.largeDocumentOptIns.length === next.largeDocumentOptIns.length &&
    prev.largeDocumentOptIns.every((document, index) => {
      const nextDocument = next.largeDocumentOptIns[index];
      return (
        document.id === nextDocument?.id &&
        document.extractionStatus === nextDocument.extractionStatus &&
        document.previewSummary === nextDocument.previewSummary
      );
    }) &&
    prev.showLargeDocumentOptInButtons === next.showLargeDocumentOptInButtons &&
    prev.isConfirmingLargeDocumentAnalysis === next.isConfirmingLargeDocumentAnalysis &&
    prev.isDecliningLargeDocumentAnalysis === next.isDecliningLargeDocumentAnalysis &&
    prev.onConfirmLargeDocumentAnalysis === next.onConfirmLargeDocumentAnalysis &&
    prev.onSubmit === next.onSubmit &&
    prev.onAbort === next.onAbort &&
    prev.isSending === next.isSending &&
    prev.isAborting === next.isAborting &&
    prev.autoFocus === next.autoFocus &&
    prev.onAttachmentsUploaded === next.onAttachmentsUploaded &&
    prev.onAttachmentRemoved === next.onAttachmentRemoved &&
    prev.allowMutations === next.allowMutations &&
    prev.onAllowMutationsChange === next.onAllowMutationsChange &&
    prev.lastUserMessage === next.lastUserMessage
);
ChatFooter.displayName = "ChatFooter";

export function ChatChrome({
  tenantId,
  conversationId,
  messages,
  activeTurn,
  streamStatus,
  loading = false,
  initialPinned = false,
  onSubmit,
  onAbort,
  isSending,
  isAborting,
  onLoadOlderMessages,
  isLoadingOlderMessages = false,
  hasOlderMessages = false,
  showFooter = true,
  inputAutoFocus = true,
  privateDocuments = [],
  largeDocumentOptIns,
  onDismissLargeDocumentOptIn = () => {},
  onConfirmLargeDocumentAnalysis = async () => {},
  isConfirmingLargeDocumentAnalysis = false,
  isDecliningLargeDocumentAnalysis = false,
  onAttachmentsUploaded,
  onAttachmentRemoved,
  allowMutations = false,
  onAllowMutationsChange,
  bottomFadeHeight,
}: {
  tenantId?: string;
  conversationId?: string;
  messages: ScoutChatMessage[];
  activeTurn?: ScoutActiveTurn;
  streamStatus?: StreamStatus;
  loading?: boolean;
  initialPinned?: boolean;
  onSubmit: ScoutSubmitHandler;
  onAbort: () => void;
  isSending: boolean;
  isAborting: boolean;
  onLoadOlderMessages?: () => Promise<void>;
  isLoadingOlderMessages?: boolean;
  hasOlderMessages?: boolean;
  showFooter?: boolean;
  inputAutoFocus?: boolean;
  privateDocuments?: ScoutConversationPrivateDocumentRow[];
  largeDocumentOptIns?: ScoutConversationPrivateDocumentRow[];
  onDismissLargeDocumentOptIn?: (privateDocumentIds: string[]) => void | Promise<void>;
  onConfirmLargeDocumentAnalysis?: (input: {
    privateDocumentIds: string[];
    documents: ScoutConversationPrivateDocumentRow[];
  }) => void | Promise<void>;
  isConfirmingLargeDocumentAnalysis?: boolean;
  isDecliningLargeDocumentAnalysis?: boolean;
  onAttachmentsUploaded?: (attachments: ScoutAttachment[]) => void;
  onAttachmentRemoved?: (privateDocumentId: string) => void | Promise<void>;
  allowMutations?: boolean;
  onAllowMutationsChange?: (next: boolean) => void;
  /**
   * Height of the bottom fade gradient that masks the messages behind the
   * input footer. Defaults to the full-page-sized responsive value; pass a
   * smaller number for compact surfaces like the popover.
   */
  bottomFadeHeight?: number;
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const messageAreaRef = useRef<HTMLDivElement>(null);
  const userScrollIntentRef = useRef(false);
  const didApplyInitialPinRef = useRef(false);
  const [isPinned, setIsPinned] = useState(initialPinned);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const awaitingLargeDocumentOptIns = largeDocumentOptIns ?? [];
  const showLargeDocumentOptInButtons =
    awaitingLargeDocumentOptIns.length > 0 &&
    isCurrentAgentTurnComplete({ activeTurn, streamStatus, messages });

  const updateJumpToLatestState = useCallback(
    (nextIsPinned = isPinned) => {
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) {
        setShowJumpToLatest(false);
        return;
      }

      const hasScrollableContent = scrollContainer.scrollHeight > scrollContainer.clientHeight + 1;
      setShowJumpToLatest(hasScrollableContent && !nextIsPinned);
    },
    [isPinned]
  );

  const scrollToLatest = useCallback((behavior: ScrollBehavior = "auto") => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    scrollContainer.scrollTo({
      top: scrollContainer.scrollHeight,
      behavior,
    });
  }, []);

  const handleJumpToLatest = useCallback(() => {
    setIsPinned(true);
    setShowJumpToLatest(false);
    requestAnimationFrame(() => {
      scrollToLatest("smooth");
    });
  }, [scrollToLatest]);

  useEffect(() => {
    if (!isPinned) {
      updateJumpToLatestState();
      return;
    }

    requestAnimationFrame(() => {
      scrollToLatest();
      updateJumpToLatestState();
    });
  }, [isPinned, messages, scrollToLatest, updateJumpToLatestState]);

  useEffect(() => {
    const messageArea = messageAreaRef.current;
    if (!messageArea || typeof ResizeObserver === "undefined") {
      return;
    }

    const handleMessageAreaResize = () => {
      if (isPinned) {
        requestAnimationFrame(() => {
          scrollToLatest();
        });
      }
      updateJumpToLatestState();
    };

    const resizeObserver = new ResizeObserver(handleMessageAreaResize);
    resizeObserver.observe(messageArea);
    return () => resizeObserver.disconnect();
  }, [isPinned, scrollToLatest, updateJumpToLatestState]);

  useEffect(() => {
    if (!initialPinned || didApplyInitialPinRef.current) {
      return;
    }

    didApplyInitialPinRef.current = true;
    setIsPinned(true);
    requestAnimationFrame(() => {
      scrollToLatest();
      updateJumpToLatestState(true);
    });
  }, [initialPinned, scrollToLatest, updateJumpToLatestState]);

  const lastRetryPayload = getLastUserTurnForRetry(messages);
  const lastUserMessage = lastRetryPayload?.text || undefined;

  const handleRetry = useCallback(() => {
    if (!lastRetryPayload || !canRetryLastUserTurn(lastRetryPayload)) {
      return;
    }

    void onSubmit(lastRetryPayload.text, {
      attachments:
        lastRetryPayload.attachments.length > 0 ? lastRetryPayload.attachments : undefined,
      attachmentPreviews:
        lastRetryPayload.attachmentPreviews.length > 0
          ? lastRetryPayload.attachmentPreviews
          : undefined,
      extractionWatchPrivateDocumentIds:
        lastRetryPayload.extractionWatchPrivateDocumentIds.length > 0
          ? lastRetryPayload.extractionWatchPrivateDocumentIds
          : undefined,
    });
  }, [lastRetryPayload, onSubmit]);

  return (
    <ScoutChatDropzone
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        ref={messageAreaRef}
        sx={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <MessageList
          messages={messages}
          activeTurn={activeTurn}
          streamStatus={streamStatus}
          onRetry={handleRetry}
          canRetry={canRetryLastUserTurn(lastRetryPayload)}
          loading={loading}
          isPinned={isPinned}
          scrollContainerRef={scrollContainerRef}
          userScrollIntentRef={userScrollIntentRef}
          onScrollStateChange={updateJumpToLatestState}
          onPin={() => setIsPinned(true)}
          onUnpin={() => setIsPinned(false)}
          onLoadOlderMessages={onLoadOlderMessages}
          isLoadingOlderMessages={isLoadingOlderMessages}
          hasOlderMessages={hasOlderMessages}
          tenantId={tenantId}
          conversationId={conversationId}
          privateDocuments={privateDocuments}
        />
        <BottomCanvasFade height={bottomFadeHeight} />
        <Stack
          direction="row"
          justifyContent="center"
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 16,
            zIndex: 3,
            opacity: showJumpToLatest ? 1 : 0,
            pointerEvents: showJumpToLatest ? "auto" : "none",
            transition: "opacity 120ms ease",
          }}
        >
          <Tooltip title="Jump to latest">
            <IconButton
              aria-label="Jump to latest Scout message"
              color="primary"
              onClick={handleJumpToLatest}
              size="small"
              sx={{
                bgcolor: "background.paper",
                border: 1,
                borderColor: "divider",
                boxShadow: 1,
                "&:hover": {
                  bgcolor: "background.paper",
                },
              }}
            >
              <KeyboardArrowDown />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      {showFooter && (
        <ChatFooter
          tenantId={tenantId}
          conversationId={conversationId}
          largeDocumentOptIns={awaitingLargeDocumentOptIns}
          showLargeDocumentOptInButtons={showLargeDocumentOptInButtons}
          onDismissLargeDocumentOptIn={onDismissLargeDocumentOptIn}
          onConfirmLargeDocumentAnalysis={onConfirmLargeDocumentAnalysis}
          isConfirmingLargeDocumentAnalysis={isConfirmingLargeDocumentAnalysis}
          isDecliningLargeDocumentAnalysis={isDecliningLargeDocumentAnalysis}
          onSubmit={onSubmit}
          onAbort={onAbort}
          isSending={isSending}
          isAborting={isAborting}
          autoFocus={inputAutoFocus}
          onAttachmentsUploaded={onAttachmentsUploaded}
          onAttachmentRemoved={onAttachmentRemoved}
          allowMutations={allowMutations}
          onAllowMutationsChange={onAllowMutationsChange}
          lastUserMessage={lastUserMessage}
        />
      )}
    </ScoutChatDropzone>
  );
}

function BottomCanvasFade({ height }: { height?: number }) {
  const canvasColor = useThemeGreyBackgroundColor();

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        left: 0,
        right: "16px",
        bottom: 0,
        zIndex: 1,
        height: height ?? { xs: 66, sm: 78 },
        pointerEvents: "none",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          maxWidth: "1000px",
          width: "100%",
          height: "100%",
          background: `linear-gradient(to bottom, ${alpha(
            canvasColor,
            0
          )} 0%, ${alpha(canvasColor, 0.9)} 48%, ${canvasColor} 72%)`,
        }}
      />
    </Box>
  );
}

function MessageList({
  messages,
  activeTurn,
  streamStatus,
  onRetry,
  canRetry,
  loading,
  isPinned,
  scrollContainerRef,
  userScrollIntentRef,
  onScrollStateChange,
  onPin,
  onUnpin,
  onLoadOlderMessages,
  isLoadingOlderMessages,
  hasOlderMessages,
  tenantId,
  conversationId,
  privateDocuments,
}: {
  tenantId?: string;
  conversationId?: string;
  messages: ScoutChatMessage[];
  activeTurn?: ScoutActiveTurn;
  streamStatus?: StreamStatus;
  onRetry: () => void;
  canRetry: boolean;
  loading: boolean;
  isPinned: boolean;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  userScrollIntentRef: RefObject<boolean>;
  onScrollStateChange: (nextIsPinned?: boolean) => void;
  onPin: () => void;
  onUnpin: () => void;
  onLoadOlderMessages?: () => Promise<void>;
  isLoadingOlderMessages: boolean;
  hasOlderMessages: boolean;
  privateDocuments?: ScoutConversationPrivateDocumentRow[];
}) {
  const theme = useTheme();
  const canvasColor = useThemeGreyBackgroundColor();
  const scrollbarThumbColor = alpha(
    theme.palette.text.primary,
    theme.palette.mode === "light" ? 0.24 : 0.34
  );
  const userScrollIntentTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const hasTriggeredTopFetchRef = useRef(false);
  const prependAnchorRef = useRef<{ scrollTop: number; scrollHeight: number } | null>(null);
  const TOP_FETCH_THRESHOLD_PX = 80;

  const clearUserScrollIntent = useCallback(() => {
    if (userScrollIntentTimeoutRef.current) {
      clearTimeout(userScrollIntentTimeoutRef.current);
      userScrollIntentTimeoutRef.current = undefined;
    }

    userScrollIntentRef.current = false;
  }, [userScrollIntentRef]);

  const markUserScrollIntent = useCallback(() => {
    if (userScrollIntentTimeoutRef.current) {
      clearTimeout(userScrollIntentTimeoutRef.current);
    }

    userScrollIntentRef.current = true;
    userScrollIntentTimeoutRef.current = setTimeout(() => {
      userScrollIntentRef.current = false;
      userScrollIntentTimeoutRef.current = undefined;
    }, 750);
  }, [userScrollIntentRef]);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (
        event.key === "ArrowUp" ||
        event.key === "PageUp" ||
        event.key === "Home" ||
        (event.key === " " && event.shiftKey)
      ) {
        markUserScrollIntent();
      }
    },
    [markUserScrollIntent]
  );

  const handleScroll = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    const isNearTop = scrollContainer.scrollTop <= TOP_FETCH_THRESHOLD_PX;
    if (!isNearTop) {
      hasTriggeredTopFetchRef.current = false;
    } else if (
      hasOlderMessages &&
      !isLoadingOlderMessages &&
      onLoadOlderMessages &&
      !hasTriggeredTopFetchRef.current
    ) {
      hasTriggeredTopFetchRef.current = true;
      prependAnchorRef.current = {
        scrollTop: scrollContainer.scrollTop,
        scrollHeight: scrollContainer.scrollHeight,
      };
      void onLoadOlderMessages();
    }

    const distanceFromBottom =
      scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
    const isAtBottom = distanceFromBottom <= 24;
    const didUserScroll = userScrollIntentRef.current;
    const didUserPin = isAtBottom && didUserScroll;
    const didUserUnpin = isPinned && !isAtBottom && didUserScroll;

    if (didUserPin) {
      onPin();
      clearUserScrollIntent();
    }

    if (didUserUnpin) {
      onUnpin();
      clearUserScrollIntent();
    }

    if (didUserPin) {
      onScrollStateChange(true);
      return;
    }

    onScrollStateChange(didUserUnpin ? false : undefined);
  }, [
    clearUserScrollIntent,
    hasOlderMessages,
    isLoadingOlderMessages,
    isPinned,
    onLoadOlderMessages,
    onPin,
    onScrollStateChange,
    onUnpin,
    scrollContainerRef,
    userScrollIntentRef,
  ]);

  useEffect(() => {
    onScrollStateChange();
  }, [messages, onScrollStateChange]);

  useLayoutEffect(() => {
    if (isLoadingOlderMessages) {
      return;
    }

    const anchor = prependAnchorRef.current;
    if (!anchor) {
      return;
    }

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      prependAnchorRef.current = null;
      return;
    }

    const deltaHeight = scrollContainer.scrollHeight - anchor.scrollHeight;
    if (deltaHeight > 0) {
      scrollContainer.scrollTop = anchor.scrollTop + deltaHeight;
    }
    prependAnchorRef.current = null;
  }, [isLoadingOlderMessages, messages, scrollContainerRef]);

  useEffect(() => clearUserScrollIntent, [clearUserScrollIntent]);

  // True only for mid-turn failures where Scout started a turn and then
  // couldn't finish. The "Scout could not start..." case is handled by
  // upstream error UX and shouldn't trigger this in-chat retry experience.
  const isMidTurnFailure =
    activeTurn?.status === "error" &&
    !!activeTurn.error &&
    activeTurn.error !== COULD_NOT_START_ERROR_MESSAGE;

  // Drop stale empty assistant placeholders — these are blank bubbles left
  // behind by a previous failed turn. Without this, clicking Retry on the
  // apology leaves the old empty placeholder in the array; once the new
  // turn starts, isMidTurnFailure flips false, the apology unrenders, and
  // the user sees the leftover blank bubble alongside the new streaming
  // placeholder.
  //
  // "Stale empty" = assistant message that's no longer streaming AND has
  // nothing the customer UI will render. Reasoning parts don't count (we
  // intentionally hide reasoning content from customers) and step_start
  // parts on their own are just placeholders — a message containing only
  // those reads as blank.
  const isStaleEmptyAssistant = (message: ScoutChatMessage): boolean => {
    if (message.role !== "assistant") return false;
    if (message.isStreaming) return false;
    const hasVisibleContent = message.parts.some((part) => {
      if (part.type === "reasoning" || part.type === "step") return false;
      if (part.type === "text") return !!part.text?.trim();
      return true;
    });
    return !hasVisibleContent;
  };

  let visibleMessages = messages.filter((message) => !isStaleEmptyAssistant(message));

  // On a mid-turn failure, also drop the trailing assistant message even if
  // it has partial content — the apology + retry stands in for it. Position-
  // based rather than id-based because the optimistic-vs-real requestId
  // reconciliation can leave the placeholder keyed unpredictably.
  if (
    isMidTurnFailure &&
    visibleMessages.length > 0 &&
    visibleMessages[visibleMessages.length - 1].role === "assistant"
  ) {
    visibleMessages = visibleMessages.slice(0, -1);
  }

  return (
    <Box
      component="main"
      ref={scrollContainerRef}
      onScroll={handleScroll}
      onWheel={markUserScrollIntent}
      onPointerDown={markUserScrollIntent}
      onTouchStart={markUserScrollIntent}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      sx={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        backgroundColor: canvasColor,
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarColor: `${scrollbarThumbColor} ${canvasColor}`,
        "&::-webkit-scrollbar": {
          backgroundColor: canvasColor,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: canvasColor,
          border: "none",
          boxShadow: "none",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: scrollbarThumbColor,
          border: "none",
          borderRadius: 999,
        },
        "&::-webkit-scrollbar-corner": {
          backgroundColor: canvasColor,
        },
      }}
    >
      <Box
        sx={{
          maxWidth: "1000px",
          width: "100%",
          mx: "auto",
          pt: { xs: 8, sm: 9 },
          pb: { xs: 4, sm: 4 },
        }}
      >
        {isLoadingOlderMessages ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", textAlign: "center", py: 1 }}
          >
            Loading older messages...
          </Typography>
        ) : null}
        {loading ? <ConversationSkeleton /> : null}
        {!loading
          ? visibleMessages.map((message) =>
              message.role === "user" ? (
                <UserMessage
                  key={message.clientInstanceId ?? message.id}
                  message={message}
                  tenantId={tenantId}
                  conversationId={conversationId}
                  privateDocuments={privateDocuments}
                />
              ) : (
                <AgentMessage
                  key={message.id}
                  message={message}
                  activeTurn={activeTurn}
                  streamStatus={streamStatus}
                />
              )
            )
          : null}
        {!loading && isMidTurnFailure ? (
          <TurnErrorMessage onRetry={onRetry} canRetry={canRetry} />
        ) : null}
      </Box>
    </Box>
  );
}

// Mirrors the outer shell of AgentMessage so the failed turn reads as a
// normal Scout message that happens to contain an apology and a retry,
// not as a separate banner appended after a blank message.
function TurnErrorMessage({ onRetry, canRetry }: { onRetry: () => void; canRetry: boolean }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        py: 1.75,
        px: { xs: 2, sm: 3 },
      }}
    >
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={1}
        sx={{
          maxWidth: "90%",
          px: 2,
          py: 1.5,
          borderRadius: 3,
          bgcolor: "transparent",
          color: "text.primary",
          overflowWrap: "break-word",
        }}
      >
        <ScoutIcon sx={{ color: "#B0BEC5", mt: 0.125 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" color="text.primary">
            {getTurnErrorMessage({ canRetry })}
          </Typography>
          {canRetry ? (
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
              <Button onClick={onRetry} variant="outlined" size="small">
                Retry
              </Button>
            </Stack>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}

function ConversationSkeleton() {
  return (
    <Stack spacing={3} sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
      <Stack alignItems="flex-end">
        <Skeleton
          variant="rounded"
          width="54%"
          height={72}
          sx={{ borderRadius: 3, bgcolor: "action.hover" }}
        />
      </Stack>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Skeleton variant="circular" width={24} height={24} />
        <Stack spacing={1} sx={{ flex: 1, maxWidth: "72%" }}>
          <Skeleton variant="text" width="86%" />
          <Skeleton variant="text" width="74%" />
          <Skeleton variant="text" width="52%" />
        </Stack>
      </Stack>
    </Stack>
  );
}
